/* ---- <spotlight-products> ---- */
(() => {
  'use strict';
  if (customElements.get('spotlight-products')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const PAGE_PUSH = 0.8;

  class SpotlightProducts extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      // Cloning the slides and measuring them waits until the section nears the viewport (theme.js).
      const near = window.theme?.whenNear;
      if (near) near(this, () => { if (this.isConnected && !this.started) this.setup(); });
      else this.setup();
    }

    setup() {
      this.started = true;
      this.track = this.querySelector('[data-splc-track]');
      if (!this.track) return;
      this.carousel = this.track.closest('ft-carousel');

      const speed = parseFloat(this.carousel?.dataset.marquee);
      const marquee = speed > 0 && !REDUCED && this.track.children.length > 1;
      this.linked = !REDUCED && !!this.track.closest('[data-scroll-linked]');
      if (this.linked && !marquee) this.linkToPage();
      else customElements.whenDefined('ft-carousel').then(() => {
        if (this.track.children.length >= 3) this.carousel?.go(1, { instant: true });
        if (marquee) this.marquee(speed, this.linked);
      });
    }

    disconnectedCallback() {
      if (this.onPage) { window.removeEventListener('scroll', this.onPage); window.removeEventListener('resize', this.onPage); }
      cancelAnimationFrame(this.tickFrame);
      this.visibility?.disconnect();
      this.resize?.disconnect();
      clearTimeout(this.touchTimer);
    }

    /* ---- Auto scroll ---- */
    marquee(speed, linked) {
      const carousel = this.carousel;
      const track = this.track;
      const real = Array.from(track.children);
      this.count = real.length;

      const copies = document.createDocumentFragment();
      real.forEach((slide) => {
        const copy = slide.cloneNode(true);
        copy.classList.add('ftc__slide-clone', 'splc__slide--copy');
        copy.removeAttribute('data-active');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
        copy.querySelectorAll('img').forEach((img) => { delete img.dataset.ftWatched; });
        copies.appendChild(copy);
      });
      track.appendChild(copies);
      window.initLazyImages?.(track);
      carousel.refresh?.();
      carousel.classList.add('is-marquee');

      const measure = () => {
        const slides = track.children;
        this.period = slides[this.count].offsetLeft - slides[0].offsetLeft;
        this.step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 0;
      };
      measure();
      this.resize = new ResizeObserver(() => measure());
      this.resize.observe(track);
      if (track.firstElementChild) this.resize.observe(track.firstElementChild);

      track.addEventListener('scrollend', () => {
        if (track.matches('.is-dragging, .is-gliding')) return;
        measure();
        if (this.period > 0 && track.scrollLeft >= this.period) {
          track.scrollLeft -= this.period;
          synced = false;
        }
      });

      let hovered = false, focused = false, touched = false, visible = false;
      carousel.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hovered = true; });
      carousel.addEventListener('pointerleave', () => { hovered = false; });
      carousel.addEventListener('focusin', () => { focused = true; });
      carousel.addEventListener('focusout', (e) => { if (!carousel.contains(e.relatedTarget)) focused = false; });
      const touchHold = () => { touched = true; clearTimeout(this.touchTimer); };
      const touchRelease = () => { clearTimeout(this.touchTimer); this.touchTimer = setTimeout(() => { touched = false; }, 2500); };
      track.addEventListener('touchstart', touchHold, { passive: true });
      track.addEventListener('touchend', touchRelease, { passive: true });
      track.addEventListener('touchcancel', touchRelease, { passive: true });
      let loaded = false;
      this.visibility = new IntersectionObserver(([e]) => {
        visible = e.isIntersecting;
        if (visible && loaded && !this.tickFrame) { last = 0; this.tickFrame = requestAnimationFrame(tick); }
      }, { threshold: 0.1 });
      this.visibility.observe(carousel);

      let pos = track.scrollLeft, last = 0, synced = true, current = -1;
      const wrap = (x) => (this.period > 0 ? ((x % this.period) + this.period) % this.period : x);

      if (linked) {
        let lastY = window.scrollY;
        this.onPage = () => {
          const y = window.scrollY;
          const delta = y - lastY;
          lastY = y;
          if (!delta || !visible || track.matches('.is-dragging, .is-gliding')) return;
          pos = wrap(track.scrollLeft + delta * PAGE_PUSH);
          track.scrollLeft = pos;
        };
        window.addEventListener('scroll', this.onPage, { passive: true });
      }

      const tick = (now) => {
        if (!visible) { this.tickFrame = 0; synced = false; return; }
        const dt = last ? Math.min(64, now - last) : 0;
        last = now;
        const held = hovered || focused || touched || !visible || document.hidden || track.matches('.is-dragging, .is-gliding');
        if (held) synced = false;
        else {
          if (!synced) { pos = track.scrollLeft; synced = true; }
          pos = wrap(pos + (speed * dt) / 1000);
          track.scrollLeft = pos;
          const i = this.step ? Math.round(pos / this.step) : 0;
          if (i !== current) {
            current = i;
            Array.from(track.children).forEach((s, k) => s.toggleAttribute('data-active', k === i));
          }
        }
        this.tickFrame = requestAnimationFrame(tick);
      };
      const start = () => { loaded = true; this.tickFrame = requestAnimationFrame(tick); };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    /* ---- Move with page scroll ---- */
    linkToPage() {
      this.carousel?.classList.add('is-linked');
      this.onPage = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.followPage());
      };
      window.addEventListener('scroll', this.onPage, { passive: true });
      window.addEventListener('resize', this.onPage);
      this.followPage();
    }

    followPage() {
      if (this.track.matches('.is-dragging, .is-gliding')) return;
      const box = window.matchMedia('(max-width: 989px)').matches ? this.track.closest('.spl__half') || this.track : this;
      const r = box.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(1, Math.max(0, (vh - r.top) / Math.max(1, r.height)));
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.track.scrollLeft = progress * max;
    }
  }

  customElements.define('spotlight-products', SpotlightProducts);
})();
